/* eslint-disable no-unused-vars, react/jsx-indent-props,react/jsx-wrap-multilines, no-undef, react/jsx-closing-bracket-location */

import React, { useEffect, useState } from "react";
import { AccessType, WithPreview } from "@quintype/components";
import { object, shape, string } from "prop-types";

import StoryWrapper from "../story-templates/story-wrapper";
import { useSelector } from "react-redux";
import get from "lodash/get";

/**
 * Check if an asset matches a story ID
 */
const assetMatchesStory = (asset, storyId) => {
  const assetId = asset.metadata?.id || asset.id;
  return String(assetId) === String(storyId);
};

/**
 * Check if user has purchased a specific story
 */
const checkUserHasPurchasedStory = (subscriptions, storyId) => {
  return subscriptions.some((subscription) => {
    const assets = subscription.assets || [];
    return assets.some((asset) => assetMatchesStory(asset, storyId));
  });
};

/**
 * Filter plans that include a specific story
 */
const filterPlansForStory = (plans, storyId) => {
  const matching = plans.filter((plan) => {
    const assets = plan.groupAssets?.length > 0 ? plan.groupAssets : (plan.assets || []);
    if (assets.length === 0) return true;
    return assets.some((asset) => assetMatchesStory(asset, storyId));
  });
  
  return matching.length > 0 ? matching : plans;
};

// Inner component that handles fetching subscription plans
function StoryPageContent({
  story,
  config,
  serverHasAccess,
  isATGlobal,
  initAccessType,
  checkAccess,
  initRazorPayPayment,
  validateCoupon,
  getSubscription,
  getSubscriptionForUser,
  accessTypeBkIntegrationId,
  member,
}) {
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [hasUserPurchasedStory, setHasUserPurchasedStory] = useState(false);
  const [isPPACheckComplete, setIsPPACheckComplete] = useState(false);

  // Check if this is a premium story template (PPA applicable)
  const premiumTemplates = ["premium-story-template", "conference", "workshop", "session"];
  const isPremiumStoryTemplate = premiumTemplates.includes(story?.["story-template"]);

  // Fetch subscription plans when AccessType is ready (same pattern as ProfilePageWithAccesstype)
  useEffect(() => {
    if (story?.id && isPremiumStoryTemplate && initAccessType && getSubscription) {
      // Reset state when checking
      setIsPPACheckComplete(false);
      
      initAccessType(() => {
        let userCheckComplete = false;
        let plansCheckComplete = false;
        
        const markComplete = () => {
          if (userCheckComplete && plansCheckComplete) {
            setIsPPACheckComplete(true);
          }
        };

        // First, get user's existing subscriptions and check if they already have access to this story
        if (getSubscriptionForUser) {
          getSubscriptionForUser()
            .then((res) => {
              const subscriptions = res?.subscriptions || [];
              const hasPurchased = checkUserHasPurchasedStory(subscriptions, story.id);
              setHasUserPurchasedStory(hasPurchased);
              userCheckComplete = true;
              markComplete();
            })
            .catch(() => {
              userCheckComplete = true;
              markComplete();
            });
        } else {
          userCheckComplete = true;
          markComplete();
        }

        getSubscription()
          .then((subscriptionGroups) => {
            const allPlans = [];

            // Loop through each subscription group and collect all plans
            subscriptionGroups.forEach((group) => {
              const subscriptionPlans = group.subscription_plans || [];

              subscriptionPlans.forEach((plan) => {
                // Keep original plan object intact, just add display properties
                allPlans.push({
                  ...plan,
                  // Keep id as number for API calls
                  subscription_plan_id: plan.id,
                  label: plan.title,
                  price: `₹ ${((plan.price_cents || 0) / 100).toLocaleString()} including GST`,
                  groupId: group.id,
                  groupName: group.name,
                  groupAssets: group.assets || [],
                });
              });
            });

            const matchingPlans = filterPlansForStory(allPlans, story.id);
            setSubscriptionPlans(matchingPlans);
            plansCheckComplete = true;
            markComplete();
          })
          .catch(() => {
            plansCheckComplete = true;
            markComplete();
          });
      });
    } else if (!isPremiumStoryTemplate) {
      // Not a premium story, mark as complete immediately
      setIsPPACheckComplete(true);
    }
  }, [global.AccessType, member, isATGlobal, story?.id, isPremiumStoryTemplate]);

  return (
    <StoryWrapper
      isATGlobal={isATGlobal}
      story={story}
      config={config}
      initAccessType={initAccessType}
      checkAccess={checkAccess}
      serverHasAccess={serverHasAccess}
      subscriptionPlans={subscriptionPlans}
      initRazorPayPayment={initRazorPayPayment}
      validateCoupon={validateCoupon}
      getSubscriptionForUser={getSubscriptionForUser}
      accessTypeBkIntegrationId={accessTypeBkIntegrationId}
      member={member}
      hasUserPurchasedStory={hasUserPurchasedStory}
      isPPACheckComplete={isPPACheckComplete}
    />
  );
}

function StoryPageBaseWithAccesstype({ story, config, hasAccess: serverHasAccess }) {
  const [isATGlobal, setIsATGlobal] = useState(false);
  const member = useSelector((state) => get(state, ["member"], null));
  const email = get(member, ["email"], "");
  const phone = get(member, ["metadata", "phone-number"], "");
  const { key, accessTypeBkIntegrationId } = useSelector((state) =>
    get(state, ["qt", "config", "publisher-attributes", "accesstypeConfig"], {})
  );

  return (
    <AccessType
      enableAccesstype={true}
      accessTypeKey={key}
      email={email}
      phone={phone}
      accessTypeBkIntegrationId={accessTypeBkIntegrationId}
      onATGlobalSet={() => {
        setIsATGlobal(true);
      }}
    >
      {({ initAccessType, checkAccess, initRazorPayPayment, validateCoupon, getSubscription, getSubscriptionForUser }) => (
        <StoryPageContent
          story={story}
          config={config}
          serverHasAccess={serverHasAccess}
          isATGlobal={isATGlobal}
          initAccessType={initAccessType}
          checkAccess={checkAccess}
          initRazorPayPayment={initRazorPayPayment}
          validateCoupon={validateCoupon}
          getSubscription={getSubscription}
          getSubscriptionForUser={getSubscriptionForUser}
          accessTypeBkIntegrationId={accessTypeBkIntegrationId}
          member={member}
        />
      )}
    </AccessType>
  );
}

StoryPageBaseWithAccesstype.propTypes = {
  story: object,
  config: object,
};

function StoryPageBase({ story, config, hasAccess }) {
  return <StoryPageBaseWithAccesstype story={story} config={config} hasAccess={hasAccess} />;
}

StoryPageBase.propTypes = {
  story: object,
  config: object,
};

const FIELDS =
  "id,headline,slug,url,hero-image-s3-key,hero-image-metadata,first-published-at,last-published-at,alternative,published-at,author-name,author-id,sections,authors,story-template,cards,access";

function storyPageLoadItems(pageNumber) {
  return global
    .wretch("/api/v1/stories")
    .query({
      fields: FIELDS,
      limit: 5,
      offset: 5 * pageNumber,
    })
    .get()
    .json((response) =>
      response.stories.map((story) => ({
        story,
        currentPath: `/${story.slug}`,
        otherProp: "value",
      }))
    );
}

export function StoryPage(props) {
  const { data } = props;
  const { story, config, hasAccess } = data;
  if (!data || !story) return null;

  const storyTemplate = story["story-template"];
  return (
    <div className={["conference", "workshop", "session", "leadership-in-focus"]?.includes(storyTemplate) ? "" : "container"}>
      <StoryPageBase story={story} config={config} hasAccess={hasAccess} />
    </div>
  );
}

StoryPage.propTypes = {
  data: shape({
    story: object,
    config: object,
  }),
  currentPath: string,
};

export const StoryPagePreview = WithPreview(StoryPage, (data, story) =>
  Object.assign({}, data, {
    story,
    relatedStories: Array(5).fill(story),
  })
);
